import type { Routes } from '@angular/router';

import { authGuard, permissionGuard, permissionGuardAny, PERMISSIONS } from '@core/core.public-api';
import { MainLayoutComponent } from '@layout/main-layout.component';

/**
 * Ruteo raiz. Todo LAZY (`loadChildren`/`loadComponent`). Convencion: cada feature expone
 * `features/<ctx>/<ctx>.routes.ts` (export default Routes).
 *
 * Defensa en profundidad:
 *  - authGuard protege TODO el area autenticada (padre MainLayoutComponent).
 *  - permissionGuard(<perm>) protege cada feature contra el catalogo PERMISSIONS (core/rbac).
 *    La UI ademas oculta el item de sidenav (nav.config.ts) — pero la autoridad real es el backend.
 *  - dashboard no tiene permiso dedicado en el catalogo: queda accesible a todo usuario
 *    autenticado (el backend filtra el contenido).
 *
 * `data.breadcrumb` alimenta el componente de breadcrumbs; `data.permission` documenta el permiso
 * exigido y debe coincidir con permissionGuard y con nav.config.ts.
 */
export const routes: Routes = [
  // Callback/silent-renew de OIDC y pantalla de login. Publico (sin authGuard).
  {
    path: 'auth',
    loadChildren: () => import('@features/auth/auth.routes'),
  },

  // Aceptacion de invitacion de tenant (LION-864). PUBLICO y TOP-LEVEL a proposito:
  // el email del primer admin linkea `${FRONTEND_URL}/aceptar-invitacion?token=...`
  // (no cuelga de /auth). El `token` viaja en la query string.
  {
    path: 'aceptar-invitacion',
    loadComponent: () =>
      import('@features/auth/ui/accept-invitation.component').then(
        (m) => m.AcceptInvitationComponent,
      ),
    title: 'Aceptar invitación',
  },

  // Area autenticada: chrome comun (topbar + sidenav + breadcrumbs) via MainLayoutComponent.
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

      {
        // Mi cuenta (LION-760: cambio de contraseña). Sin permission guard: cualquier
        // usuario autenticado gestiona su propia cuenta (el backend valida la cuenta).
        path: 'account',
        loadChildren: () => import('@features/account/account.routes'),
        data: { breadcrumb: 'Mi cuenta' },
      },

      {
        path: 'dashboard',
        loadChildren: () => import('@features/dashboard/dashboard.routes'),
        data: { breadcrumb: 'Dashboard' },
      },
      {
        path: 'live-map',
        loadChildren: () => import('@features/live-map/live-map.routes'),
        canActivate: [permissionGuard(PERMISSIONS.LIVEMAP_READ)],
        data: { breadcrumb: 'Mapa en vivo', permission: PERMISSIONS.LIVEMAP_READ },
      },
      {
        path: 'trips',
        loadChildren: () => import('@features/trips/trips.routes'),
        canActivate: [permissionGuard(PERMISSIONS.TRIPS_READ)],
        data: { breadcrumb: 'Viajes', permission: PERMISSIONS.TRIPS_READ },
      },
      {
        path: 'bookings',
        loadChildren: () => import('@features/bookings/bookings.routes'),
        canActivate: [permissionGuard(PERMISSIONS.BOOKINGS_READ)],
        data: { breadcrumb: 'Reservas', permission: PERMISSIONS.BOOKINGS_READ },
      },
      {
        path: 'telemetry',
        loadChildren: () => import('@features/telemetry/telemetry.routes'),
        canActivate: [permissionGuard(PERMISSIONS.TELEMETRY_READ)],
        data: { breadcrumb: 'Alertas de conduccion', permission: PERMISSIONS.TELEMETRY_READ },
      },
      {
        path: 'mdvr',
        loadChildren: () => import('@features/mdvr/mdvr.routes'),
        canActivate: [permissionGuard(PERMISSIONS.MDVR_READ)],
        data: { breadcrumb: 'Visor MDVR', permission: PERMISSIONS.MDVR_READ },
      },
      {
        path: 'fleet',
        loadChildren: () => import('@features/fleet/fleet.routes'),
        canActivate: [permissionGuard(PERMISSIONS.FLEET_READ)],
        data: { breadcrumb: 'Flota', permission: PERMISSIONS.FLEET_READ },
      },
      {
        path: 'drivers',
        loadChildren: () => import('@features/drivers/drivers.routes'),
        canActivate: [permissionGuard(PERMISSIONS.DRIVERS_READ)],
        data: { breadcrumb: 'Choferes', permission: PERMISSIONS.DRIVERS_READ },
      },
      {
        path: 'documents',
        loadChildren: () => import('@features/documents/documents.routes'),
        canActivate: [permissionGuard(PERMISSIONS.DOCUMENTS_READ)],
        data: { breadcrumb: 'Centro documental', permission: PERMISSIONS.DOCUMENTS_READ },
      },
      {
        path: 'routes',
        loadChildren: () => import('@features/routes/routes.routes'),
        canActivate: [permissionGuard(PERMISSIONS.ROUTE_READ)],
        data: { breadcrumb: 'Rutas y tramos', permission: PERMISSIONS.ROUTE_READ },
      },
      {
        // Document Compliance Gate (LION-784): worklist + excepciones (US2/US3).
        path: 'compliance',
        loadChildren: () => import('@features/compliance/compliance.routes'),
        canActivate: [permissionGuard(PERMISSIONS.COMPLIANCE_DASHBOARD_READ)],
        data: { breadcrumb: 'Cumplimiento documental', permission: PERMISSIONS.COMPLIANCE_DASHBOARD_READ },
      },
      {
        path: 'checklists',
        loadChildren: () => import('@features/checklists/checklists.routes'),
        canActivate: [permissionGuard(PERMISSIONS.CHECKLIST_READ)], // QA-013: operativo, no hse:read
        data: { breadcrumb: 'Checklists', permission: PERMISSIONS.CHECKLIST_READ },
      },
      {
        path: 'journey-night',
        loadChildren: () => import('@features/journey-night/journey-night.routes'),
        // QA-004 decouple: entra quien puede SOLICITAR (JOURNEY_CREATE=trip:write,
        // p.ej. operador) o APROBAR (JOURNEY_APPROVE). Aprobar en sí queda gateado
        // por JOURNEY_APPROVE dentro de la tabla (columna Acciones).
        canActivate: [permissionGuardAny(PERMISSIONS.JOURNEY_CREATE, PERMISSIONS.JOURNEY_APPROVE)],
        data: { breadcrumb: 'Journey nocturno', permission: PERMISSIONS.JOURNEY_APPROVE },
      },
      {
        path: 'incidents',
        loadChildren: () => import('@features/incidents/incidents.routes'),
        canActivate: [permissionGuard(PERMISSIONS.INCIDENTS_READ)],
        data: { breadcrumb: 'Incidentes', permission: PERMISSIONS.INCIDENTS_READ },
      },
      {
        // Modulo con datos de salud (EPS): la vista de salud se segrega adentro con EPS_HEALTH_READ.
        // Guard OR: entra HSE (hse:read, ve la pantalla bloqueada) O el servicio medico
        // (eps:read, unico rol del seed con acceso a los datos — NO tiene hse:read).
        path: 'hse-eps',
        loadChildren: () => import('@features/hse-eps/hse-eps.routes'),
        canActivate: [permissionGuardAny(PERMISSIONS.HSE_READ, PERMISSIONS.EPS_HEALTH_READ)],
        data: { breadcrumb: 'HSE / EPS', permission: PERMISSIONS.HSE_READ, sensitive: true },
      },
      {
        path: 'discipline',
        loadChildren: () => import('@features/discipline/discipline.routes'),
        canActivate: [permissionGuard(PERMISSIONS.DISCIPLINE_READ)],
        data: { breadcrumb: 'Disciplina', permission: PERMISSIONS.DISCIPLINE_READ },
      },
      {
        path: 'kpis',
        loadChildren: () => import('@features/kpis/kpis.routes'),
        canActivate: [permissionGuard(PERMISSIONS.KPIS_READ)],
        data: { breadcrumb: 'KPI / SLA', permission: PERMISSIONS.KPIS_READ },
      },
      {
        path: 'reports',
        loadChildren: () => import('@features/reports/reports.routes'),
        canActivate: [permissionGuard(PERMISSIONS.REPORTS_READ)],
        data: { breadcrumb: 'Reporteria', permission: PERMISSIONS.REPORTS_READ },
      },
      {
        path: 'billing',
        loadChildren: () => import('@features/billing/billing.routes'),
        canActivate: [permissionGuard(PERMISSIONS.BILLING_READ)],
        data: { breadcrumb: 'Facturacion', permission: PERMISSIONS.BILLING_READ },
      },
      {
        path: 'penalties',
        loadChildren: () => import('@features/penalties/penalties.routes'),
        canActivate: [permissionGuard(PERMISSIONS.PENALTIES_READ)],
        data: { breadcrumb: 'Penalidades', permission: PERMISSIONS.PENALTIES_READ },
      },
      {
        path: 'notifications',
        loadChildren: () => import('@features/notifications/notifications.routes'),
        canActivate: [permissionGuard(PERMISSIONS.NOTIFICATIONS_READ)],
        data: { breadcrumb: 'Notificaciones', permission: PERMISSIONS.NOTIFICATIONS_READ },
      },
      {
        path: 'admin',
        loadChildren: () => import('@features/admin/admin.routes'),
        canActivate: [permissionGuard(PERMISSIONS.ADMIN_USERS)],
        data: { breadcrumb: 'Administracion', permission: PERMISSIONS.ADMIN_USERS },
      },
      {
        // Empresas (tenants) del super-admin PVP (LION-864): alta + reenvio de invitacion.
        path: 'empresas',
        loadChildren: () => import('@features/empresas/empresas.routes'),
        canActivate: [permissionGuard(PERMISSIONS.ADMIN_TENANTS)],
        data: { breadcrumb: 'Empresas', permission: PERMISSIONS.ADMIN_TENANTS },
      },
    ],
  },

  // Paginas de sistema (dentro del shell para conservar la navegacion).
  {
    path: 'forbidden',
    loadComponent: () =>
      import('@layout/pages/forbidden.component').then((m) => m.ForbiddenComponent),
    data: { breadcrumb: 'Sin acceso' },
  },
  {
    path: '**',
    loadComponent: () =>
      import('@layout/pages/not-found.component').then((m) => m.NotFoundComponent),
    data: { breadcrumb: 'No encontrado' },
  },
];
